import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  SalaryByMonthResponse,
  SalaryByMonthItem,
  PayrollTestResponse,
  PayrollTestItem,
  PayrollReportResponse,
  PayrollReportItem,
  GeneratePayrollPayload,
  SalaryById,
} from '@/api/types/payroll.types'
import type { ApiResponse } from '@/api/types'

/**
 * Convert YYYY-MM (input month) to MM-YYYY for API (e.g. 2026-02 -> 02-2026)
 */
export function toApiMonthYear(ym: string): string {
  if (!ym || ym.length < 7) return ym
  const [y, m] = ym.split('-')
  return `${m}-${y}`
}

/**
 * GET GetSalaryByMonth – salary list by month/year
 * API expects monthYear as MM-YYYY (e.g. 02-2026)
 */
export async function getSalaryByMonth(
  branchId: number,
  monthYear: string,
  pageNumber = 1,
  pageSize = 50
): Promise<SalaryByMonthResponse> {
  const apiMonthYear = toApiMonthYear(monthYear)
  const url = endpoints.payroll.getSalaryByMonth(branchId, apiMonthYear, pageNumber, pageSize)
  const res = await apiClient.get<ApiResponse<SalaryByMonthResponse> | SalaryByMonthResponse>(url)
  const raw = res.data
  const arr: any[] =
    raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)
      ? (raw as any).data
      : raw && typeof raw === 'object' && Array.isArray((raw as any).data)
        ? (raw as any).data
        : []

  const mapItem = (item: any): SalaryByMonthItem => ({
    id: item.id ?? item.Id,
    empId: item.empId ?? item.EmpId ?? item.employeeId ?? item.EmployeeId,
    employeeId: item.employeeId ?? item.EmployeeId ?? item.empId ?? item.EmpId,
    employeeName: item.employeeName ?? item.EmployeeName,
    empCode: item.empCode ?? item.EmpCode,
    monthYear: item.monthYear ?? item.MonthYear ?? item.salaryMonthYear ?? item.SalaryMonthYear,
    salaryMonthYear: item.salaryMonthYear ?? item.SalaryMonthYear ?? item.monthYear ?? item.MonthYear,
    totalSalary: item.totalSalary ?? item.TotalSalary,
    payrollDate: item.payrollDate ?? item.PayrollDate ?? item.date ?? item.Date,
    // Allowances (API may use camelCase or PascalCase; note typo transpotation)
    basicSalary: item.basicSalary ?? item.BasicSalary,
    housingAllowance: item.housingAllowance ?? item.HousingAllowance,
    transpotationAllowance: item.transpotationAllowance ?? item.TranspotationAllowance ?? item.transportationAllowance ?? item.TransportationAllowance,
    mealAllowance: item.mealAllowance ?? item.MealAllowance,
    otherAllowance: item.otherAllowance ?? item.OtherAllowance,
    totalAllowance: item.totalAllowance ?? item.TotalAllowance,
    // Deductions
    gosiDeduction: item.gosiDeduction ?? item.GosiDeduction ?? item.gosi ?? item.Gosi,
    absentDeduction: item.absentDeduction ?? item.AbsentDeduction,
    otherDeductions: item.otherDeductions ?? item.OtherDeductions ?? item.otherDeduction ?? item.OtherDeduction,
    totalDeduction: item.totalDeduction ?? item.TotalDeduction,
    department: item.department ?? item.Department,
    project: item.project ?? item.Project,
  })

  return {
    data: arr.map(mapItem),
    totalRecords: (raw as any)?.totalRecords ?? arr.length,
    pageNumber: (raw as any)?.pageNumber ?? pageNumber,
    pageSize: (raw as any)?.pageSize ?? pageSize,
  }
}

/**
 * GET GetReport – payroll report by fromMonth/toMonth
 * fromMonth, toMonth: YYYY-MM (converted to MM-YYYY for API)
 */
export async function getPayrollReport(
  branchId: number,
  fromMonth: string,
  toMonth: string,
  pageNumber = 1,
  pageSize = 50
): Promise<PayrollReportResponse> {
  const apiFrom = toApiMonthYear(fromMonth)
  const apiTo = toApiMonthYear(toMonth)
  const url = endpoints.payroll.getReport({ branchId, fromMonth: apiFrom, toMonth: apiTo, pageNumber, pageSize })
  const res = await apiClient.get<ApiResponse<PayrollReportResponse> | PayrollReportResponse>(url)
  const raw = res.data as any
  let arr: any[] = []
  if (Array.isArray(raw)) {
    arr = raw
  } else if (raw && typeof raw === 'object' && Array.isArray(raw.data)) {
    arr = raw.data
  }
  const totalRec = raw?.totalRecords ?? arr.length
  const pNum = raw?.pageNumber ?? pageNumber
  const pSize = raw?.pageSize ?? pageSize
  const mapItem = (item: any): PayrollReportItem => ({
    empId: item.empId ?? item.EmpId,
    empRef: item.empRef ?? item.EmpRef,
    empCode: item.empCode ?? item.EmpCode,
    employeeName: item.employeeName ?? item.EmployeeName,
    department: item.department ?? item.Department,
    projectRef: item.projectRef ?? item.ProjectRef,
    project: item.project ?? item.Project,
    monthYear: item.monthYear ?? item.MonthYear ?? null,
    contractSalary: item.contractSalary ?? item.ContractSalary ?? 0,
    basicSalary: item.basicSalary ?? item.BasicSalary ?? 0,
    housingAllowance: item.housingAllowance ?? item.HousingAllowance ?? 0,
    transportationAllowance: item.transportationAllowance ?? item.TransportationAllowance ?? 0,
    mealAllowance: item.mealAllowance ?? item.MealAllowance ?? 0,
    otherAllowance: item.otherAllowance ?? item.OtherAllowance ?? 0,
    totalAllowance: item.totalAllowance ?? item.TotalAllowance ?? 0,
    grossSalary: item.grossSalary ?? item.GrossSalary ?? 0,
    bonus: item.bonus ?? item.Bonus ?? 0,
    incentive: item.incentive ?? item.Incentive ?? 0,
    loanDeduction: item.loanDeduction ?? item.LoanDeduction ?? 0,
    absentDays: item.absentDays ?? item.AbsentDays ?? 0,
    absentDeduction: item.absentDeduction ?? item.AbsentDeduction ?? 0,
    gosi: item.gosi ?? item.Gosi ?? 0,
    otherDeduction: item.otherDeduction ?? item.OtherDeduction ?? 0,
    nonRecurringDeduction: item.nonRecurringDeduction ?? item.NonRecurringDeduction ?? 0,
    totalDeduction: item.totalDeduction ?? item.TotalDeduction ?? 0,
    netSalary: item.netSalary ?? item.NetSalary ?? 0,
  })
  return {
    data: arr.map(mapItem),
    totalRecords: totalRec,
    pageNumber: pNum,
    pageSize: pSize,
  }
}

/**
 * GET GetTest – test payroll (detailed list)
 * startDate, endDate: YYYY-MM-DD or ISO date string
 */
export async function getPayrollTest(params: {
  branchId: number
  monthYear: string
  startDate: string
  endDate: string
  empId?: number
  pageNumber?: number
  pageSize?: number
}): Promise<PayrollTestResponse> {
  const { branchId, monthYear, startDate, endDate, empId = 0, pageNumber = 1, pageSize = 50 } = params
  const apiMonthYear = toApiMonthYear(monthYear)
  const url = endpoints.payroll.getTest({
    monthYear: apiMonthYear,
    startDate,
    endDate,
    branchId,
    empId,
    pageNumber,
    pageSize,
  })
  const res = await apiClient.get<ApiResponse<PayrollTestResponse> | PayrollTestResponse>(url)
  const raw = res.data
  const arr: any[] =
    raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)
      ? (raw as any).data
      : raw && typeof raw === 'object' && Array.isArray((raw as any).data)
        ? (raw as any).data
        : []

  // Map API keys only (camelCase/PascalCase); no calculations – list shows API data as-is
  const mapItem = (item: any): PayrollTestItem => ({
    empId: item.empId ?? item.EmpId,
    empRef: item.empRef ?? item.EmpRef,
    empCode: item.empCode ?? item.EmpCode,
    employeeName: item.employeeName ?? item.EmployeeName,
    department: item.department ?? item.Department,
    projectRef: item.projectRef ?? item.ProjectRef,
    project: item.project ?? item.Project,
    monthYear: item.monthYear ?? item.MonthYear,
    contractSalary: item.contractSalary ?? item.ContractSalary,
    basicSalary: item.basicSalary ?? item.BasicSalary,
    housingAllowance: item.housingAllowance ?? item.HousingAllowance,
    transportationAllowance: item.transportationAllowance ?? item.TransportationAllowance,
    mealAllowance: item.mealAllowance ?? item.MealAllowance,
    otherAllowance: item.otherAllowance ?? item.OtherAllowance,
    totalAllowance: item.totalAllowance ?? item.TotalAllowance,
    grossSalary: item.grossSalary ?? item.GrossSalary,
    bonus: item.bonus ?? item.Bonus,
    incentive: item.incentive ?? item.Incentive,
    loanDeduction: item.loanDeduction ?? item.LoanDeduction,
    absentDays: item.absentDays ?? item.AbsentDays,
    absentDeduction: item.absentDeduction ?? item.AbsentDeduction,
    gosi: item.gosi ?? item.Gosi,
    otherDeduction: item.otherDeduction ?? item.OtherDeduction,
    nonRecurringDeduction: item.nonRecurringDeduction ?? item.NonRecurringDeduction,
    totalDeduction: item.totalDeduction ?? item.TotalDeduction,
    netSalary: item.netSalary ?? item.NetSalary,
  })

  return {
    data: arr.map(mapItem),
    totalRecords: (raw as any)?.totalRecords ?? arr.length,
    pageNumber: (raw as any)?.pageNumber ?? pageNumber,
    pageSize: (raw as any)?.pageSize ?? pageSize,
  }
}

/**
 * GET GetSalaryById – fetch single salary/pay slip by id
 */
export async function getSalaryById(id: number): Promise<SalaryById> {
  const url = endpoints.payroll.getSalaryById(id)
  const res = await apiClient.get<SalaryById>(url)
  const raw = res.data
  if (raw && typeof raw === 'object') {
    return raw as SalaryById
  }
  return raw as SalaryById
}

/**
 * POST GeneratePayroll – generate/post payroll
 * monthYear: YYYY-MM; startDate/endDate: ISO or YYYY-MM-DD
 */
export async function generatePayroll(payload: GeneratePayrollPayload): Promise<unknown> {
  const url = endpoints.payroll.generatePayroll
  const res = await apiClient.post(url, payload)
  return res.data
}
